import { CommonButton } from 'components/common/Forms/Button';
import { useGoBack, usePatientBooking } from 'context/patient/modules/booking';
import { useMemo, useState } from 'react';
import { FaClipboardList } from 'react-icons/fa';

import Select from 'components/common/Forms/Select';
import { InlineConfirm } from 'components/Popup/ConfirmDialog/InlineConfirm';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';
import { ApptOption } from 'types/apptOptions';
import { SelectOption } from 'types/common';

/**
 * Value of the "None of these apply" item. It is not sent to the backend,
 * the appointment simply keeps no appointment option.
 */
const NONE_VALUE = '';

interface Props {
  apptOptions: ApptOption[];
}

export const PatientBookingPickApptOption = ({ apptOptions }: Props) => {
  const { t } = useCommonTranslation();
  const { updateBooking } = usePatientBooking();
  const { canGoBack, goBack } = useGoBack();
  const [selected, setSelected] = useState(NONE_VALUE);

  const options: SelectOption[] = useMemo(
    () => [
      { value: NONE_VALUE, label: t('appointment-option-none') },
      ...apptOptions.map(({ id, name }) => ({ value: id, label: name })),
    ],
    [apptOptions, t],
  );

  const handleNext = () => {
    updateBooking({
      stage: 'day',
      apptOptionId: selected === NONE_VALUE ? undefined : selected,
    });
  };

  return (
    <InlineConfirm
      icon={<FaClipboardList />}
      title={t('appointment-option-question')}
      description={t('appointment-option-description')}
      ariaLabel={t('appointment-option-question')}
      actions={[
        <CommonButton
          key="next"
          variant="primary"
          size="large"
          onClick={handleNext}
        >
          {t('next')}
        </CommonButton>,
        ...(canGoBack
          ? [
              <CommonButton
                key="back"
                variant="secondary"
                size="large"
                onClick={goBack}
              >
                {t('back')}
              </CommonButton>,
            ]
          : []),
      ]}
      inlineIcon
      actionsTopMargin
    >
      <Select
        label={t('appointment-option')}
        id="appt-option"
        name="appt-option"
        options={options}
        value={selected}
        onChange={(value) => setSelected((value as string) ?? NONE_VALUE)}
        required
      />
    </InlineConfirm>
  );
};
